import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';
import {
  roleService,
  type CreateRoleRequest,
  type UpdateRoleRequest,
} from '@/services/roleService';

export function useRolesList(page = 1, pageSize = 20) {
  return useQuery({
    queryKey: queryKeys.roles.list(page, pageSize),
    queryFn: () => roleService.getRoles(page, pageSize),
  });
}

// Convenience for pickers/selects that need every role in one shot.
// `enabled` lets callers skip the fetch when the user lacks `roles.view`
// (the endpoint is roles.view-gated → would otherwise 403 + toast).
export function useAllRoles(enabled = true) {
  return useQuery({
    queryKey: queryKeys.roles.list(1, 100),
    queryFn: () => roleService.getRoles(1, 100),
    select: (data) => data.items,
    enabled,
  });
}

export function usePermissionGroups() {
  return useQuery({
    queryKey: queryKeys.permissions.groups(),
    queryFn: () => roleService.getPermissions(),
    staleTime: 15 * 60 * 1000,
  });
}

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateRoleRequest) => roleService.createRole(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.roles.all }),
  });
}

export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRoleRequest }) =>
      roleService.updateRole(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.roles.all }),
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => roleService.deleteRole(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.roles.all }),
  });
}
